import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';
import { useFinanceFilters } from '../lib/FinanceFilters.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReimbursementCase {
  id: number;
  reimbursementId: string | null;
  caseId: string | null;
  orderId: string;
  sku: string;
  fnsku: string;
  asin: string;
  reason: string;
  amount: number;
  currency: string;
  qtyReimbursed: number;
  approvalDate: string | null;
  detectionDate: string | null;
  status: string;
  expectedAmount: number | null;
  notes: string | null;
}

interface Stats {
  totalReceived: number;
  totalMissing: number;
  totalFiled: number;
  totalResolved: number;
  countReceived: number;
  countMissing: number;
  countFiled: number;
  countResolved: number;
  countExpired: number;
}

interface CasesResponse {
  cases: ReimbursementCase[];
  total: number;
}

interface Account {
  id: string;
  name: string;
}

type StatusFilter = '' | 'received' | 'missing' | 'filed' | 'resolved' | 'expired';

// ── Component ────────────────────────────────────────────────────────────────

export function FinanceReimbursements() {
  const [cases, setCases] = useState<ReimbursementCase[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const { displayName } = useNicknames();
  const filters = useFinanceFilters('30');

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      const active = accts.filter(a => (a as any).is_active !== 0);
      setAccounts(active);
      if (active.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (accounts.length === 0) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, statusFilter, filters.currency]);

  function loadData() {
    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('limit', '100');
    const qs = params.toString();

    Promise.all(
      accounts.flatMap(a => [
        api.get<CasesResponse>(`/finance/reimbursements/${a.id}?${qs}${filters.currencyParam}`).catch(() => ({ cases: [], total: 0 })),
        api.get<Stats>(`/finance/reimbursements/${a.id}/stats${filters.currencyParam ? '?' + filters.currencyParam.slice(1) : ''}`).catch(() => null),
      ])
    ).then(results => {
      const allCases: ReimbursementCase[] = [];
      const mergedStats: Stats = { totalReceived: 0, totalMissing: 0, totalFiled: 0, totalResolved: 0, countReceived: 0, countMissing: 0, countFiled: 0, countResolved: 0, countExpired: 0 };
      for (let i = 0; i < results.length; i += 2) {
        const casesData = results[i] as CasesResponse;
        const statsData = results[i + 1] as Stats | null;
        allCases.push(...casesData.cases);
        if (statsData) {
          mergedStats.totalReceived += statsData.totalReceived;
          mergedStats.totalMissing += statsData.totalMissing;
          mergedStats.totalFiled += statsData.totalFiled;
          mergedStats.totalResolved += statsData.totalResolved;
          mergedStats.countReceived += statsData.countReceived;
          mergedStats.countMissing += statsData.countMissing;
          mergedStats.countFiled += statsData.countFiled;
          mergedStats.countResolved += statsData.countResolved;
          mergedStats.countExpired += statsData.countExpired;
        }
      }
      setCases(allCases);
      setStats(mergedStats);
    }).catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }

  async function handleScan() {
    setScanning(true);
    setScanMsg('');
    try {
      const msgs: string[] = [];
      for (const a of accounts) {
        const result = await api.post<{ message: string }>(`/finance/reimbursements/${a.id}/scan`);
        msgs.push(`${a.name}: ${result.message}`);
      }
      setScanMsg(msgs.join(' | '));
    } catch (err) {
      setScanMsg(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }

  async function updateStatus(caseId: number, newStatus: string) {
    try {
      await api.put(`/finance/reimbursements/${caseId}`, { status: newStatus });
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  if (loading) return <LoadingSkeleton />;

  const statusTabs: { value: StatusFilter; label: string; count?: number }[] = [
    { value: '', label: 'All' },
    { value: 'received', label: 'Received', count: stats?.countReceived },
    { value: 'missing', label: 'Missing', count: stats?.countMissing },
    { value: 'filed', label: 'Filed', count: stats?.countFiled },
    { value: 'resolved', label: 'Resolved', count: stats?.countResolved },
    { value: 'expired', label: 'Expired', count: stats?.countExpired },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Reimbursement Scanner <span className="text-xs font-normal text-gray-400 ml-2">All amounts in {filters.currencyLabel}</span></h2>
          <p className="text-sm text-gray-500">Track received and detect missing FBA reimbursements.</p>
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {scanning ? 'Scanning...' : 'Run Scanner'}
        </button>
      </div>

      {/* Feedback */}
      {scanMsg && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {scanMsg}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Received" value={fmtCurrency(stats.totalReceived)} className="text-green-600" />
          <StatCard label="Missing / Recoverable" value={fmtCurrency(stats.totalMissing)} className="text-red-600" />
          <StatCard label="Filed" value={fmtCurrency(stats.totalFiled)} className="text-amber-600" />
          <StatCard label="Resolved" value={fmtCurrency(stats.totalResolved)} className="text-blue-600" />
        </div>
      )}

      {/* Status Tabs */}
      <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              statusFilter === tab.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span className="ml-1 text-gray-400">({tab.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {cases.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm text-gray-500">No reimbursement cases found.</p>
          <p className="mt-1 text-xs text-gray-400">Click "Run Scanner" to import and detect reimbursements.</p>
        </div>
      )}

      {/* Cases Table */}
      {cases.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cases.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {formatDate(c.approvalDate ?? c.detectionDate)}
                  </td>
                  <td className="px-4 py-3" title={c.sku || undefined}>
                    <span className="font-medium">{c.sku ? displayName(c.sku) : '—'}</span>
                    {c.asin && <span className="ml-1 text-xs text-gray-400">({c.asin})</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate" title={c.reason}>
                    {c.reason || '—'}
                  </td>
                  <td className="px-4 py-3 text-right">{c.qtyReimbursed}</td>
                  <td className="px-4 py-3 text-right font-medium">
                    {c.status === 'missing'
                      ? fmtCurrency(c.expectedAmount ?? 0)
                      : fmtCurrency(c.amount)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {c.status === 'missing' && (
                        <button
                          onClick={() => updateStatus(c.id, 'filed')}
                          className="text-xs text-amber-600 hover:text-amber-800"
                        >
                          Mark Filed
                        </button>
                      )}
                      {c.status === 'filed' && (
                        <button
                          onClick={() => updateStatus(c.id, 'resolved')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Mark Resolved
                        </button>
                      )}
                      {(c.status === 'missing' || c.status === 'filed') && (
                        <button
                          onClick={() => updateStatus(c.id, 'expired')}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Expire
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    received: 'bg-green-100 text-green-700',
    missing: 'bg-red-100 text-red-700',
    filed: 'bg-amber-100 text-amber-700',
    resolved: 'bg-blue-100 text-blue-700',
    expired: 'bg-gray-100 text-gray-500',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${className ?? ''}`}>{value}</p>
    </div>
  );
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
    </div>
  );
}
